import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import adminConsoleService from '@services/adminConsoleService';

interface Holiday {
  HolidayId: number;
  Date: string;
  Description: string | null;
}

const ALL_DAYS = [
  { key: 'monday', label: 'M' },
  { key: 'tuesday', label: 'T' },
  { key: 'wednesday', label: 'W' },
  { key: 'thursday', label: 'T' },
  { key: 'friday', label: 'F' },
  { key: 'saturday', label: 'S' },
  { key: 'sunday', label: 'S' },
];

function formatDate(iso: string): string
{
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

// ─── Add Holiday Modal ────────────────────────────────────────────────────────

interface AddHolidayModalProps {
  saving: boolean;
  error: string | null;
  onConfirm: (date: string) => void;
  onClose: () => void;
}

// Custom input for DatePicker — matches the project's SubmitTime style
const DateInput = React.forwardRef<
  HTMLInputElement,
  { value?: string; onClick?: () => void; placeholder?: string }
    >(({ value, onClick, placeholder }, ref) => (
      <div className="relative flex items-center">
        <input
          ref={ref}
          type="text"
          value={value || ''}
          onClick={onClick}
          onChange={() => {}}
          readOnly
          placeholder={placeholder || 'MM/DD/YYYY'}
          className="w-full border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] pr-8 cursor-pointer bg-white"
        />
        <button
          type="button"
          onClick={onClick}
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-[#888] hover:text-[#5697b3]"
        >
          <span className="material-icons text-[16px]">calendar_today</span>
        </button>
      </div>
    ));
DateInput.displayName = 'DateInput';

const AddHolidayModal: React.FC<AddHolidayModalProps> = ({ saving, error, onConfirm, onClose }) =>
{
  const [date, setDate] = useState<Date | null>(null);

  const handleConfirm = () =>
  {
    if (!date) return;
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    onConfirm(iso);
  };

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[360px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Add New Holiday</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828]">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Holiday Date:</label>
            <DatePicker
              selected={date}
              onChange={(d: Date | null) => setDate(d)}
              dateFormat="MM/dd/yyyy"
              customInput={<DateInput />}
              popperPlacement="bottom-start"
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              calendarClassName="custom-datepicker-calendar"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] dark:bg-[#5c6060] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !date}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Delete Holiday Modal ─────────────────────────────────────────────────────

interface DeleteHolidayModalProps {
  holiday: Holiday;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

const DeleteHolidayModal: React.FC<DeleteHolidayModalProps> = ({
  holiday,
  saving,
  error,
  onConfirm,
  onClose,
}) =>
  createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[360px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Delete Holiday</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        <div className="px-5 py-4 text-[12px] text-[#555]">
          {error && (
            <div className="mb-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[#c62828]">
              {error}
            </div>
          )}
          Are you sure you want to delete this date{' '}
          <span className="font-medium">"{formatDate(holiday.Date)}"</span>?
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] dark:bg-[#5c6060] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const CompanyConfigurationsTab: React.FC = () =>
{
  // Holidays
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Group holidays by year, sorted descending; collapse years older than last 3
  const groupedByYear = useMemo(() =>
  {
    const map = new Map<number, Holiday[]>();
    holidays.forEach(h =>
    {
      const y = new Date(h.Date).getFullYear();
      if (!map.has(y)) map.set(y, []);
      map.get(y)!.push(h);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, items]) => ({
        year,
        items: items.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()),
      }));
  }, [holidays]);

  // Collapse all years except the current year on load
  useEffect(() =>
  {
    if (groupedByYear.length === 0) return;
    const currentYear = new Date().getFullYear();
    setCollapsedYears(new Set(groupedByYear.filter(g => g.year !== currentYear).map(g => g.year)));
  }, [groupedByYear.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleYear = (year: number) =>
  {
    setCollapsedYears(prev =>
    {
      const next = new Set(prev);
      next.has(year) ? next.delete(year) : next.add(year);
      return next;
    });
  };

  // Working config
  const [workingHours, setWorkingHours] = useState('8');
  const [workingDays, setWorkingDays] = useState<string[]>([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const loadHolidays = useCallback(async () =>
  {
    try
    {
      const data = await adminConsoleService.getHolidays();
      setHolidays(data);
    }
    catch
    {
      // non-fatal
    }
    finally
    {
      setHolidaysLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    loadHolidays();
    adminConsoleService
      .getCompanyConfig()
      .then(cfg =>
      {
        setWorkingHours(cfg.WorkingHours ?? '8');
        setWorkingDays(
          cfg.WorkingDays
            ? cfg.WorkingDays.split(',')
              .map(d => d.trim().toLowerCase())
              .filter(Boolean)
            : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        );
      })
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, [loadHolidays]);

  // ── Holidays ──────────────────────────────────────────────────────────────

  const handleAddHoliday = async (dateValue: string) =>
  {
    setAddSaving(true);
    setAddError(null);
    try
    {
      await adminConsoleService.addHoliday(dateValue);
      setShowAdd(false);
      setHolidaysLoading(true);
      await loadHolidays();
    }
    catch
    {
      setAddError('Failed to add holiday. Please try again.');
    }
    finally
    {
      setAddSaving(false);
    }
  };

  const handleDeleteHoliday = async () =>
  {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    setDeleteError(null);
    try
    {
      await adminConsoleService.deleteHoliday(deleteTarget.HolidayId);
      setDeleteTarget(null);
      setHolidaysLoading(true);
      await loadHolidays();
    }
    catch
    {
      setDeleteError('Failed to delete holiday. Please try again.');
    }
    finally
    {
      setDeleteSaving(false);
    }
  };

  // ── Working config ────────────────────────────────────────────────────────

  const toggleDay = (key: string) =>
  {
    setWorkingDays(prev => (prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]));
  };

  const handleSaveConfig = async () =>
  {
    setConfigSaving(true);
    setConfigError(null);
    setConfigSaved(false);
    try
    {
      await adminConsoleService.updateCompanyConfig({
        WorkingHours: workingHours,
        Workingdays: workingDays.join(','),
      });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    }
    catch
    {
      setConfigError('Failed to save configuration.');
    }
    finally
    {
      setConfigSaving(false);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* Page header */}
      <h3 className="text-[16px] font-normal text-[#5697b3] mb-1">Company Configurations</h3>
      <p className="text-[12px] text-[#888] mb-5">
        This view will allow to setup the holidays and working days default of your company.
      </p>

      <div className="flex gap-5 items-start">
        {/* ── Left: Holidays ───────────────────────────────────────────────── */}
        <div className="flex-1 border border-[#ddd] rounded-sm bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e8e8] bg-[#fafafa] dark:bg-[#181818]">
            <span className="text-[13px] font-medium text-[#5697b3]">Holidays</span>
            <button
              onClick={() =>
              {
                setAddError(null);
                setShowAdd(true);
              }}
              className="text-[12px] text-[#5697b3] flex items-center gap-1"
            >
              <span className="material-icons !text-[px] dark:text-[#beb7ac] ">
                add_circle_outline
              </span>
              <span className="hover:underline">Add New Holiday</span>
            </button>
          </div>

          {holidaysLoading ? (
            <div className="flex items-center gap-2 p-5 text-[12px] text-[#aaa]">
              <span className="material-icons animate-spin text-[16px] text-[#62aacb]">
                refresh
              </span>
              Loading…
            </div>
          ) : holidays.length === 0 ? (
            <p className="text-center py-6 text-[12px] text-[#aaa]">No holidays configured.</p>
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                  <th className="text-left px-4 py-2 text-[#555] font-medium">Date</th>
                  <th className="text-right px-4 py-2 text-[#555] font-medium w-[80px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedByYear.map(({ year, items }) =>
                {
                  const isCollapsed = collapsedYears.has(year);
                  return (
                    <React.Fragment key={year}>
                      {/* Year header row */}
                      <tr
                        className="bg-[#eef5fb] border-b border-[#dde8f0] cursor-pointer select-none hover:bg-[#e3eef8] transition-colors"
                        onClick={() => toggleYear(year)}
                      >
                        <td className="px-4 py-1.5 text-[#5697b3] font-medium flex items-center gap-1.5">
                          <span className="material-icons text-[14px] text-[#5697b3]">
                            {isCollapsed ? 'chevron_right' : 'expand_more'}
                          </span>
                          {year}
                          <span className="text-[11px] text-[#99b8cc] font-normal ml-1">
                            ({items.length})
                          </span>
                        </td>
                        <td />
                      </tr>
                      {/* Holiday rows */}
                      {!isCollapsed &&
                        items.map((h, i) => (
                          <tr
                            key={h.HolidayId}
                            className={`border-b border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f0f6fb] transition-colors`}
                          >
                            <td className="px-4 py-2 pl-9 text-[#555]">{formatDate(h.Date)}</td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() =>
                                {
                                  setDeleteError(null);
                                  setDeleteTarget(h);
                                }}
                                className="px-3 py-1 bg-[#e57373] text-white rounded text-[11px] hover:bg-[#d32f2f] dark:bg-[#8d312c] dark:hover:brightness-[0.85] transition-colors"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Right: Working Days ──────────────────────────────────────────── */}
        <div className="w-[240px] border border-[#ddd] rounded-sm bg-white shadow-sm">
          <div className="px-4 py-3 border-b border-[#e8e8e8] bg-[#fafafa] dark:bg-[#181818]">
            <span className="text-[13px] font-medium text-[#5697b3] ">Working Days</span>
          </div>

          {configLoading ? (
            <div className="flex items-center gap-2 p-4 text-[12px] text-[#aaa]">
              <span className="material-icons animate-spin text-[16px] text-[#62aacb]">
                refresh
              </span>
              Loading…
            </div>
          ) : (
            <div className="px-4 py-4 flex flex-col gap-4">
              {/* Day toggles */}
              <div className="flex gap-1 justify-between">
                {ALL_DAYS.map(d =>
                {
                  const active = workingDays.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      onClick={() => toggleDay(d.key)}
                      title={d.key.charAt(0).toUpperCase() + d.key.slice(1)}
                      className={`w-[28px] h-[28px] rounded text-[11px] font-medium transition-colors border ${
                        active
                          ? 'bg-[#b3d9f0] border-[#62aacb] text-[#2d6f8f] dark:text-[#fff] dark:bg-[#3c6c80] dark:hover:brightness-[0.85]'
                          : 'bg-white border-[#d0d0d0] text-[#aaa] dark:!text-[#a9a093] dark:!bg-[#363838] dark:hover:brightness-[0.85]'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>

              {/* Working hours */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] text-[#5697b3]">Working Hours Per Day</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={workingHours}
                  onChange={e => setWorkingHours(e.target.value)}
                  className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] w-full"
                />
              </div>

              {/* Save */}
              <button
                onClick={handleSaveConfig}
                disabled={configSaving}
                className="w-full py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
              >
                {configSaving ? 'Saving…' : configSaved ? 'Saved!' : 'Save Changes'}
              </button>

              {configError && <p className="text-[11px] text-[#e57373]">{configError}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Add Holiday Modal */}
      {showAdd && (
        <AddHolidayModal
          saving={addSaving}
          error={addError}
          onConfirm={handleAddHoliday}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Delete Holiday Modal */}
      {deleteTarget && (
        <DeleteHolidayModal
          holiday={deleteTarget}
          saving={deleteSaving}
          error={deleteError}
          onConfirm={handleDeleteHoliday}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
